// Merchant Sales Report (MM-3037): client store for the partially loaded merchant tree.
// Rows live once in `byId`; structure lives in `children` (parent -> ordered child ids). The table renders
// the flat list from flattenVisible(), which is what keeps virtualization, Sub Total rows and "Load more"
// rows on one code path.
import { ChildrenPage, MerchantRow } from "./types";

/** Key of the top level, which holds only the selected merchant. */
export const ROOT = "__root__";

export type VisibleRow =
  | { kind: "node"; id: string; depth: number }
  | { kind: "subtotal"; id: string; depth: number }
  | { kind: "more"; parentId: string; depth: number; loaded: number; total: number }
  | { kind: "loading"; parentId: string; depth: number };

export class TreeStore {
  byId = new Map<string, MerchantRow>();
  children = new Map<string, string[]>();
  childTotal = new Map<string, number>();
  /** Cursor for the next page of a parent's children; absent or null once everything is loaded. */
  nextCursor = new Map<string, string | null>();
  expanded = new Set<string>();
  /** Parents with a children request in flight. */
  loading = new Set<string>();
  rootId: string | null = null;

  setRoot(row: MerchantRow) {
    this.byId.set(row.id, row);
    this.children.set(ROOT, [row.id]);
    this.childTotal.set(ROOT, 1);
    this.rootId = row.id;
  }

  /** Adds one page of children. Ids already present are not repeated. */
  appendChildren(parentId: string, page: ChildrenPage) {
    const list = this.children.get(parentId) ?? [];
    for (const row of page.items) {
      if (!list.includes(row.id)) list.push(row.id);
      this.byId.set(row.id, row);
    }
    this.children.set(parentId, list);
    this.childTotal.set(parentId, page.total_count);
    this.nextCursor.set(parentId, page.has_more ? page.next_cursor : null);
  }

  hasChildrenLoaded(id: string) {
    return this.children.has(id);
  }

  hasMore(parentId: string) {
    return !!this.nextCursor.get(parentId);
  }

  flattenVisible(): VisibleRow[] {
    const out: VisibleRow[] = [];
    // Recursion depth is bounded by the hierarchy depth (at most 5 merchant levels).
    const visit = (parentId: string, depth: number) => {
      const kids = this.children.get(parentId) ?? [];
      for (const id of kids) {
        out.push({ kind: "node", id, depth });
        if (this.expanded.has(id) && this.hasChildrenLoaded(id)) {
          visit(id, depth + 1);
          // Figma: "Σ Subtotal of <merchant>" closes every expanded group. The figures are the merchant's
          // subtree, served by the API, so they are right even while only the first page is loaded. The
          // selected merchant gets none: the pinned Grand Total is its subtree.
          if (id !== this.rootId) out.push({ kind: "subtotal", id, depth });
        }
      }
      if (parentId === ROOT) return;
      if (this.loading.has(parentId) && kids.length > 0) {
        out.push({ kind: "loading", parentId, depth });
      } else if (this.hasMore(parentId)) {
        out.push({ kind: "more", parentId, depth, loaded: kids.length, total: this.childTotal.get(parentId) ?? kids.length });
      }
    };
    visit(ROOT, 0);
    return out;
  }
}
